import React, { useState, useEffect } from 'react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { ChevronRight, ChevronDown, Folder, HardDrive, X } from 'lucide-react';
import { FileStat } from '../../api/IFileSystem';
import { t } from '../../i18n';

interface TreeNodeProps {
  file: FileStat;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  fs: any;
}

const TreeNode: React.FC<TreeNodeProps> = ({ file, selectedPath, onSelect, fs }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [children, setChildren] = useState<FileStat[]>([]);
  const [loading, setLoading] = useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExpanded) {
      setIsExpanded(false);
    } else {
      setLoading(true);
      try {
        const items = await fs.readdir(file.path);
        // Filter only directories
        const dirs = items.filter((f: FileStat) => f.type === 'dir' || f.type === 'drive');
        setChildren(dirs);
        setIsExpanded(true);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(file.path);
  };

  const isSelected = selectedPath === file.path;

  return (
    <div className="pl-4">
      <div 
        className={`flex items-center py-1 px-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-win-accent text-white' : 'hover:bg-white/10 text-slate-300'}`}
        onClick={handleSelect}
      >
        <div onClick={handleToggle} className="p-1 mr-1 hover:bg-white/20 rounded">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
        
        {file.type === 'drive' ? <HardDrive size={16} className="mr-2 text-slate-400" /> : <Folder size={16} className="mr-2 text-yellow-500" />}
        <span className="text-sm truncate select-none">{file.name}</span>
      </div>

      {isExpanded && (
        <div className="border-l border-white/10 ml-2">
          {children.map(child => (
            <TreeNode 
              key={child.path} 
              file={child} 
              selectedPath={selectedPath} 
              onSelect={onSelect} 
              fs={fs} 
            />
          ))}
          {children.length === 0 && !loading && (
             <div className="pl-8 py-1 text-xs text-slate-500 italic">{t('noSubfolders')}</div>
          )}
        </div>
      )}
    </div>
  );
};

const FolderPickerModal: React.FC = () => {
  const { folderPickerState, closeFolderPicker, fs } = useFileManagerStore();
  const [drives, setDrives] = useState<FileStat[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  useEffect(() => {
    if (folderPickerState?.isOpen) {
      // Load drives initially
      fs.getDrives().then(setDrives);
      // Default select current?
      // setSelectedPath(folderPickerState.initialPath); // If we pass it
    }
  }, [folderPickerState?.isOpen, fs]);

  if (!folderPickerState?.isOpen) return null;

  const handleConfirm = () => {
    if (selectedPath) {
      folderPickerState.onConfirm(selectedPath);
      closeFolderPicker();
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-[#202020] w-[400px] h-[500px] border border-white/10 rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#2d2d2d]">
          <span className="font-medium text-slate-200">{folderPickerState.title || t('selectFolder')}</span>
          <button onClick={closeFolderPicker} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Tree View */}
        <div className="flex-1 overflow-auto p-2 bg-[#1e1e1e]">
          {drives.map(drive => (
            <TreeNode 
              key={drive.path} 
              file={drive} 
              selectedPath={selectedPath} 
              onSelect={setSelectedPath} 
              fs={fs} 
            />
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 bg-[#2d2d2d] flex justify-end space-x-2">
          <button 
            onClick={closeFolderPicker}
            className="px-4 py-2 text-xs rounded bg-white/5 hover:bg-white/10 text-slate-300 transition-colors"
          >
            {t('cancel')}
          </button>
          <button 
            onClick={handleConfirm}
            disabled={!selectedPath}
            className="px-4 py-2 text-xs rounded bg-win-accent hover:bg-blue-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('selectFolder')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolderPickerModal;
